<template>
  <app-modal @click-outside="onClose">
    <div class="modal-content">
      <app-icon-button :size="32" class="close-button" @click="onClose">
        <fa-icon-compat class="icon" icon="fa-regular fa-xmark" />
      </app-icon-button>

      <div
        :style="{ 'background-color': resources.accentColor }"
        class="side-panel"
      >
        <app-image
          v-if="resources.image"
          :src="resources.image"
          :width="'fit-content'"
          class="app-image"
        />
      </div>
      <div class="main">
        <div class="challenge-modal-title">{{ resources.title }}</div>

        <div class="tagline">{{ resources.tagline }}</div>

        <app-section :level="2" :title="$t('modal.challenge.description')">
          {{ resources.description }}
        </app-section>

        <template v-if="resources.instructions">
          <app-section :level="2" :title="$t('modal.challenge.required')">
            <ul v-if="Array.isArray(resources.instructions)">
              <li v-for="line in resources.instructions" :key="line">
                {{ line }}
              </li>
            </ul>
            <template v-else>
              {{ resources.instructions }}
            </template>
          </app-section>
        </template>

        <app-section
          v-if="reward"
          :level="2"
          :title="$t('modal.challenge.rewards')"
        >
          {{ reward }}
        </app-section>

        <div class="buttons">
          <app-button
            v-if="resources.button"
            :label="resources.button.label"
            class="challenge-button"
            @click="goToUrl(resources.button.href)"
          />
        </div>
      </div>
    </div>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import type {
  IBadgeResource,
  IChallenge,
  IChallengeResource,
  IVirtualGood
} from '@rready/sdk';

export default defineComponent({
  name: 'ChallengeModal',
  props: {
    challenge: {
      type: Object as PropType<IChallenge>,
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    resources(): IChallengeResource {
      return this.$res(this.challenge.achievement.resources);
    },
    reward(): string {
      return this.challenge?.achievement?.reward?.virtualGoods
        .map(
          (vg: IVirtualGood<IBadgeResource>) =>
            `${this.$res(vg.resources).title} ${this.$t('common.badge')}`
        )
        .join(', ');
    }
  },
  methods: {
    goToUrl(url: string) {
      window.location.assign(url);
    },
    onClose() {
      this.$emit('close');
    }
  }
});
</script>

<style lang="scss" scoped>
@use '@rready/design/dist/assets/utils.scss';

$modal-width: 796px;

::v-deep .app-modal {
  width: $modal-width;
  max-width: 90%;
  height: fit-content;
  position: relative;
}

.modal-content {
  padding: 24px;
  display: flex;
  flex-direction: row;
  height: 100%;
  gap: var(--space-4);
  color: var(--color-grey-dark);
}

.close-button {
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  font-weight: 600;
}

.side-panel {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 33%;
  padding: var(--space-5);
  background-color: var(--color-grey-light);
  border-radius: var(--radius-3);
}

.main {
  width: 65%;
  font-size: var(--font-size-small);
}

.app-image {
  width: 120px !important;
}

.challenge-button {
  width: fit-content;
  padding: 0 16px;
}

.challenge-modal-title {
  font-weight: 600;
  font-size: var(--font-size-large);
  margin-bottom: var(--space-1);
  color: var(--color-black);
  @include utils.line-clamp(1);
}

.app-section {
  margin-bottom: var(--space-2);
  margin-top: var(--space-3);
}

.buttons {
  margin-top: var(--space-4);
}

@media only screen and (max-width: $modal-width) {
  ::v-deep .app-modal {
    width: 100%;
  }
  .side-panel {
    display: none;
  }

  .main {
    width: 100%;
  }
}
</style>
